import React,{useState,useEffect,useCallback} from 'react';
import {inventoryAPI,branchAPI,productAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput,fmt,fmtDate,FilterButtons} from '../components/common';
import {Plus,Eye,Edit,Trash2,CheckCircle,XCircle,Truck,Package,
  ArrowRight,RefreshCw,X,Send,RotateCcw,Clock} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
import api from '../utils/api';

// ── API ──────────────────────────────────────────────────────────
const transferAPI = {
  getAll:  p  => api.get('/stock-transfers', {params:p}),
  getById: id => api.get(`/stock-transfers/${id}`),
  create:  d  => api.post('/stock-transfers', d),
  update:  (id,d) => api.put(`/stock-transfers/${id}`, d),
  approve: id => api.patch(`/stock-transfers/${id}/approve`),
  ship:    id => api.patch(`/stock-transfers/${id}/ship`),
  receive: (id,d) => api.patch(`/stock-transfers/${id}/receive`, d),
  cancel:  id => api.patch(`/stock-transfers/${id}/cancel`),
  delete:  id => api.delete(`/stock-transfers/${id}`),
};

const ST_COLOR={draft:'warning',pending:'info',in_transit:'accent',received:'success',cancelled:'danger'};
const ST_LABEL={draft:'Draft',pending:'Pending Approval',in_transit:'In Transit',received:'Received',cancelled:'Cancelled'};
const STATUSES=Object.keys(ST_LABEL);

// ── Build UoM options from a product (base + conversions) ────────
function buildProductUomOpts(prod) {
  if (!prod) return [];
  const opts = [];
  if (prod.uom) {
    opts.push({
      value: String(prod.uom.id || prod.uom_id),
      label: `${prod.uom.name} (${prod.uom.abbreviation}) — Base`,
      price: parseFloat(prod.sale_price) || 0,
      factor: 1,
    });
  }
  (prod.uoms || []).forEach(pu => {
    if (!pu.uom) return;
    opts.push({
      value: String(pu.uom.id || pu.uom_id),
      label: `${pu.uom.name} (${pu.uom.abbreviation}) ×${pu.conversion_factor}`,
      price: parseFloat(pu.price) || 0,
      factor: parseFloat(pu.conversion_factor) || 1,
    });
  });
  return opts;
}

// ── Transfer Item Row ────────────────────────────────────────────
function ItemRow({row,idx,products,onChange,onRemove,inventoryMap,canSeeCost}){
  const prod = products.find(p=>p.id===parseInt(row.product_id));
  const availableStock = inventoryMap?.[row.product_id] || 0;
  const prodUomOpts = buildProductUomOpts(prod);
  const overQty = row.product_id && parseFloat(row.quantity) > availableStock;

  const handleProductChange = e => {
    const p = products.find(x=>x.id===parseInt(e.target.value));
    onChange(idx, 'product_id', e.target.value);
    if (p) {
      const baseUomId = p.uom ? String(p.uom.id || p.uom_id) : '';
      onChange(idx, 'uom_id', baseUomId);
      onChange(idx, 'unit_cost', parseFloat(p.cost_price)||0);
    } else {
      onChange(idx, 'uom_id', '');
      onChange(idx, 'unit_cost', 0);
    }
  };

  const handleUomChange = e => {
    const opt = prodUomOpts.find(o=>o.value===e.target.value);
    onChange(idx, 'uom_id', e.target.value);
    if (opt) onChange(idx, 'unit_cost', opt.price);
  };

  return(
    <tr style={{borderBottom:'1px solid var(--border)'}}>
      <td style={{padding:'6px 8px',minWidth:220}}>
        <select className="form-select" value={String(row.product_id||'')}
          onChange={handleProductChange} style={{fontSize:12}}>
          <option value="">— Select Product —</option>
          {products.map(p=><option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
        </select>
      </td>
      <td style={{padding:'6px 8px',minWidth:160}}>
        {prodUomOpts.length > 0 ? (
          <select className="form-select" value={String(row.uom_id||'')}
            onChange={handleUomChange} style={{fontSize:12}}>
            <option value="">— UoM —</option>
            {prodUomOpts.map(u=>(
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        ) : (
          <span style={{fontSize:11,color:'var(--td)',padding:'0 4px'}}>
            {prod ? (prod.uom?.abbreviation || '—') : '—'}
          </span>
        )}
      </td>
      <td style={{padding:'6px 8px',width:80,textAlign:'center',fontSize:12,color:'var(--td)'}}>
        {row.product_id&&(
          <span style={{fontSize:11}}>
            Stock: <strong style={{color:availableStock>0?'var(--success)':'var(--danger)'}}>{availableStock}</strong>
          </span>
        )}
      </td>
      <td style={{padding:'6px 8px',width:100}}>
        <input type="number" min={1} className="form-input"
          value={row.quantity||''}
          onChange={e=>onChange(idx,'quantity',e.target.value)}
          style={{textAlign:'right',fontSize:13,
            borderColor:overQty?'var(--danger)':undefined}}/>
      </td>
      <td style={{padding:'6px 8px',width:100}}>
        {canSeeCost ? (
          <input type="number" min={0} step={0.01} className="form-input" value={row.unit_cost||''}
            onChange={e=>onChange(idx,'unit_cost',e.target.value)}
            style={{textAlign:'right',fontSize:13}}/>
        ) : (
          <span style={{fontSize:11,color:'var(--td)',padding:'0 4px'}}>—</span>
        )}
      </td>
      <td style={{padding:'6px 8px',width:36}}>
        <button type="button" className="btn btn-ghost btn-sm"
          onClick={()=>onRemove(idx)}
          style={{color:'var(--danger)',padding:4,width:28,justifyContent:'center'}}>
          <X size={13}/>
        </button>
      </td>
    </tr>
  );
}

// ── Receive Modal ────────────────────────────────────────────────
function ReceiveModal({open,transfer,onClose,onDone}){
  const[lines,setLines]=useState([]);
  const[saving,setSaving]=useState(false);

  useEffect(()=>{
    if(transfer?.items){
      setLines(transfer.items.map(it=>({
        id:it.id,
        product_name:it.product?.name||'',
        sku:it.product?.sku||'',
        quantity:it.quantity,
        receiving:it.quantity,
      })));
    }
  },[transfer]);

  if(!open||!transfer) return null;

  const doReceive=async()=>{
    setSaving(true);
    try{
      await transferAPI.receive(transfer.id,{items:lines.map(l=>({id:l.id,received_qty:parseFloat(l.receiving)||0}))});
      toast.success('Stock received at destination branch!');
      onDone(); onClose();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
    finally{setSaving(false);}
  };

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'var(--surface)',borderRadius:14,border:'1px solid var(--border)',width:'100%',maxWidth:580,maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid var(--border)'}}>
          <div style={{fontWeight:700,fontSize:15,display:'flex',alignItems:'center',gap:8}}>
            <Package size={18} color="var(--primary)"/>
            Receive Transfer — {transfer.transfer_number}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{overflowY:'auto',padding:20,flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'var(--s2)',borderRadius:8,marginBottom:16,fontSize:13}}>
            <span style={{fontWeight:700}}>{transfer.fromBranch?.name||'—'}</span>
            <ArrowRight size={16} color="var(--primary)"/>
            <span style={{fontWeight:700,color:'var(--primary)'}}>{transfer.toBranch?.name||'—'}</span>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'var(--s2)'}}>
                {['Product','SKU','Transferred','Receiving'].map(h=>(
                  <th key={h} style={{padding:'8px 10px',textAlign:['Transferred','Receiving'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l,i)=>(
                <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 10px',fontWeight:600}}>{l.product_name}</td>
                  <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:11,color:'var(--primary)'}}>{l.sku}</td>
                  <td style={{padding:'8px 10px',textAlign:'right'}}>{l.quantity}</td>
                  <td style={{padding:'8px 10px'}}>
                    <input type="number" min={0} max={l.quantity} className="form-input"
                      value={l.receiving}
                      onChange={e=>setLines(arr=>{const n=[...arr];n[i]={...n[i],receiving:e.target.value};return n;})}
                      style={{textAlign:'right',width:80}}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{padding:'14px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={doReceive} disabled={saving}>
            {saving?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Saving…</>:<><CheckCircle size={14}/> Confirm Receipt</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Modal ─────────────────────────────────────────────────
function DetailModal({open,transfer,onClose,canSeeCost}){
  if(!open||!transfer) return null;
  const total=(transfer.items||[]).reduce((s,i)=>s+(parseFloat(i.unit_cost)||0)*(i.quantity||0),0);
  return(
    <Modal open={open} onClose={onClose} title={`Transfer — ${transfer.transfer_number}`} width={680}>
      {/* Route header */}
      <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'var(--s2)',borderRadius:10,marginBottom:16,border:'1px solid var(--border)'}}>
        <div style={{flex:1,textAlign:'center'}}>
          <div style={{fontSize:10,color:'var(--td)',fontWeight:700,marginBottom:3}}>FROM</div>
          <div style={{fontSize:15,fontWeight:800}}>{transfer.fromBranch?.name||'—'}</div>
        </div>
        <ArrowRight size={22} color="var(--primary)"/>
        <div style={{flex:1,textAlign:'center'}}>
          <div style={{fontSize:10,color:'var(--td)',fontWeight:700,marginBottom:3}}>TO</div>
          <div style={{fontSize:15,fontWeight:800,color:'var(--primary)'}}>{transfer.toBranch?.name||'—'}</div>
        </div>
        <Badge variant={ST_COLOR[transfer.status]||'info'} style={{alignSelf:'center'}}>
          {ST_LABEL[transfer.status]||transfer.status}
        </Badge>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14}}>
        {[
          ['Created',fmtDate(transfer.createdAt)],
          ['Shipped',transfer.shipped_at?fmtDate(transfer.shipped_at):'—'],
          ['Received',transfer.received_at?fmtDate(transfer.received_at):'—'],
        ].map(([k,v])=>(
          <div key={k} style={{background:'var(--s2)',borderRadius:8,padding:'10px 12px'}}>
            <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:4}}>{k.toUpperCase()}</div>
            <div style={{fontSize:13,fontWeight:600}}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{overflowX:'auto',borderRadius:8,border:'1px solid var(--border)',marginBottom:14}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{background:'var(--s2)'}}>
              {['Product','SKU','UoM','Qty','Received',...(canSeeCost?['Unit Cost','Total']:[])].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:['Qty','Received','Unit Cost','Total'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(transfer.items||[]).map((it,i)=>(
              <tr key={i} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                <td style={{padding:'8px 10px',fontWeight:600}}>{it.product?.name||'—'}</td>
                <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:11,color:'var(--primary)'}}>{it.product?.sku||'—'}</td>
                <td style={{padding:'8px 10px',fontSize:12,color:'var(--td)'}}>{it.uom?.abbreviation||it.uom?.name||'—'}</td>
                <td style={{padding:'8px 10px',textAlign:'right'}}>{it.quantity}</td>
                <td style={{padding:'8px 10px',textAlign:'right',color:it.received_qty>=it.quantity?'var(--success)':'var(--td)'}}>{it.received_qty||0}</td>
                {canSeeCost&&<td style={{padding:'8px 10px',textAlign:'right'}}>{fmt(it.unit_cost)}</td>}
                {canSeeCost&&<td style={{padding:'8px 10px',textAlign:'right',fontWeight:700}}>{fmt((it.unit_cost||0)*(it.quantity||0))}</td>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{background:'var(--s2)'}}>
              {canSeeCost ? (
                <>
                  <td colSpan={6} style={{padding:'10px',textAlign:'right',fontWeight:700}}>Total Value:</td>
                  <td style={{padding:'10px',textAlign:'right',fontWeight:800,fontSize:15,color:'var(--primary)'}}>{fmt(total)}</td>
                </>
              ) : (
                <td colSpan={5} style={{padding:'10px',textAlign:'right',color:'var(--td)',fontSize:12,fontStyle:'italic'}}>Cost price hidden</td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
      {transfer.notes&&<div style={{padding:12,background:'var(--s2)',borderRadius:8,fontSize:13,color:'var(--td)',marginBottom:14}}><strong>Notes:</strong> {transfer.notes}</div>}
      <div className="modal-footer"><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

// ── Main Page ────────────────────────────────────────────────────
export default function StockTransferPage(){
  const{isOwner,myBranchId,hasPermission}=useAuth();
  const canSeeCost   = hasPermission('cost_price.view');
  const canCreate    = hasPermission('stock_transfers.create');
  const canApprove   = hasPermission('stock_transfers.approve');
  const canReceive   = hasPermission('stock_transfers.receive');
  const canDelete    = hasPermission('stock_transfers.delete');
  const[data,setData]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  const[statusFilter,setStatusFilter]=useState('All');
  const[branches,setBranches]=useState([]);
  const[products,setProducts]=useState([]);
  const[inventoryMap,setInventoryMap]=useState({});// product_id -> qty at from_branch
  const[modal,setModal]=useState(null);
  const[selTransfer,setSelTransfer]=useState(null);
  const[delId,setDelId]=useState(null);
  const[saving,setSaving]=useState(false);

  const blankForm={
    from_branch_id:myBranchId||'',
    to_branch_id:'',
    notes:'',
    items:[{product_id:'',uom_id:'',quantity:1,unit_cost:0}],
  };
  const[form,setForm]=useState(blankForm);

  useEffect(()=>{
    Promise.all([
      branchAPI.getAll({limit:100}),
      productAPI.getAll({limit:500}),
    ]).then(([b,p])=>{
      setBranches(b.data.data||b.data);
      setProducts(p.data.data||p.data);
    }).catch(()=>{});
  },[]);

  // Load inventory for from-branch so we can show available stock
  useEffect(()=>{
    if(!form.from_branch_id) return;
    inventoryAPI.getAll({branch_id:form.from_branch_id,limit:500}).then(r=>{
      const inv=(r.data.data||r.data||[]);
      const m={};
      inv.forEach(i=>{ m[i.product_id]=(m[i.product_id]||0)+i.quantity; });
      setInventoryMap(m);
    }).catch(()=>setInventoryMap({}));
  },[form.from_branch_id]);

  const load=useCallback(async()=>{
    try{
      const params={search,limit:100};
      if(statusFilter!=='All') params.status=statusFilter;
      const r=await transferAPI.getAll(params);
      setData(r.data.data||r.data||[]);
    }catch{ setData([]); }
    finally{setLd(false);}
  },[search,statusFilter]);

  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t);},[load]);

  const F=(k,v)=>setForm(f=>({...f,[k]:v}));
  const updateItem=(idx,key,val)=>setForm(f=>({...f,items:f.items.map((it,i)=>i===idx?{...it,[key]:val}:it)}));
  const removeItem=idx=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}));
  const addItem=()=>setForm(f=>({...f,items:[...f.items,{product_id:'',uom_id:'',quantity:1,unit_cost:0}]}));

  const save=async()=>{
    if(!form.from_branch_id) return toast.error('From branch required');
    if(!form.to_branch_id) return toast.error('To branch required');
    if(form.from_branch_id===form.to_branch_id) return toast.error('From and To branch must be different');
    const validItems=form.items.filter(it=>it.product_id&&parseFloat(it.quantity)>0);
    if(!validItems.length) return toast.error('Add at least one product');

    // Check stock availability
    for(const it of validItems){
      const avail=inventoryMap[it.product_id]||0;
      if(parseFloat(it.quantity)>avail){
        const prod=products.find(p=>p.id===parseInt(it.product_id));
        return toast.error(`Insufficient stock for ${prod?.name||'product'} (available: ${avail})`);
      }
    }

    setSaving(true);
    try{
      const payload={
        from_branch_id:parseInt(form.from_branch_id),
        to_branch_id:parseInt(form.to_branch_id),
        notes:form.notes||'',
        status:'pending', // newly created goes to pending approval
        items:validItems.map(it=>({
          product_id:parseInt(it.product_id),
          uom_id:parseInt(it.uom_id)||null,
          quantity:parseFloat(it.quantity)||1,
          unit_cost:parseFloat(it.unit_cost)||0,
        })),
      };
      if(modal==='new') await transferAPI.create(payload);
      else await transferAPI.update(form.id,payload);
      toast.success(modal==='new'?'Transfer created — pending approval':'Updated');
      setModal(null); load();
    }catch(e){toast.error(e.response?.data?.error||'Failed to save');}
    finally{setSaving(false);}
  };

  const doAction=async(transfer,action)=>{
    try{
      if(action==='approve') await transferAPI.approve(transfer.id);
      else if(action==='ship') await transferAPI.ship(transfer.id);
      else if(action==='cancel') await transferAPI.cancel(transfer.id);
      toast.success(`Transfer ${action}d`);
      load();
    }catch(e){
      // Fallback: use generic update if custom endpoint missing
      try{
        const statusMap={approve:'pending',ship:'in_transit',cancel:'cancelled'};
        await transferAPI.update(transfer.id,{status:action==='approve'?'pending':action==='ship'?'in_transit':'cancelled'});
        toast.success('Status updated');
        load();
      }catch(e2){toast.error(e2.response?.data?.error||'Failed');}
    }
  };

  const openView=async(t)=>{
    try{const r=await transferAPI.getById(t.id);setSelTransfer(r.data);setModal('view');}
    catch{toast.error('Failed to load');}
  };
  const openReceive=async(t)=>{
    try{const r=await transferAPI.getById(t.id);setSelTransfer(r.data);setModal('receive');}
    catch{toast.error('Failed to load');}
  };
  const openEdit=async(t)=>{
    try{
      const r=await transferAPI.getById(t.id);
      const p=r.data;
      setForm({
        ...p,
        from_branch_id:String(p.from_branch_id||''),
        to_branch_id:String(p.to_branch_id||''),
        items:(p.items||[]).map(it=>({product_id:String(it.product_id||''),uom_id:String(it.uom_id||''),quantity:it.quantity,unit_cost:it.unit_cost||0})),
      });
      setModal('edit');
    }catch{toast.error('Failed to load');}
  };

  const del=async()=>{
    try{await transferAPI.delete(delId);toast.success('Deleted');setDelId(null);load();}
    catch(e){toast.error(e.response?.data?.error||'Cannot delete');}
  };

  const branchOpts=[{value:'',label:'— Select Branch —'},...branches.map(b=>({value:String(b.id),label:b.name}))];
  const filterOpts=['All',...STATUSES];

  return(
    <div>
      <PageHeader
        title="Stock Transfers"
        subtitle={`${data.length} transfers`}
        action={
          canCreate && (
            <button className="btn btn-primary"
              onClick={()=>{setForm({...blankForm,from_branch_id:String(myBranchId||'')});setModal('new');}}>
              <Plus size={16}/> New Transfer
            </button>
          )
        }
      />

      <div className="filter-bar">
        <div className="flex-1" style={{minWidth:200}}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search transfer #, branch..."/>
        </div>
        <FilterButtons options={filterOpts} active={statusFilter} onChange={setStatusFilter}/>
      </div>

      <DataTable loading={ld} data={data} columns={[
        {key:'transfer_number',label:'Transfer #',render:r=><span className="text-accent" style={{fontFamily:'monospace',fontSize:12}}>{r.transfer_number||`TRF-${r.id}`}</span>},
        {key:'route',label:'Route',render:r=>(
          <div style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
            <span style={{fontWeight:600}}>{r.fromBranch?.name||'—'}</span>
            <ArrowRight size={13} color="var(--primary)"/>
            <span style={{fontWeight:700,color:'var(--primary)'}}>{r.toBranch?.name||'—'}</span>
          </div>
        )},
        {key:'items',label:'Items',render:r=><Badge variant="info">{(r.items||[]).length}</Badge>},
        {key:'created',label:'Created',render:r=>fmtDate(r.createdAt)},
        {key:'shipped_at',label:'Shipped',render:r=>r.shipped_at?fmtDate(r.shipped_at):<span className="text-dim">—</span>},
        {key:'received_at',label:'Received',render:r=>r.received_at?fmtDate(r.received_at):<span className="text-dim">—</span>},
        {key:'status',label:'Status',render:r=>(
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <Badge variant={ST_COLOR[r.status]||'info'}>{ST_LABEL[r.status]||r.status}</Badge>
          </div>
        )},
      ]} actions={r=>(
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={()=>openView(r)} title="View"><Eye size={14}/></button>

          {/* Edit: only draft/pending + canCreate */}
          {canCreate&&['draft','pending'].includes(r.status)&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(r)} title="Edit"><Edit size={14}/></button>
          )}

          {/* Approve */}
          {canApprove&&r.status==='pending'&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>doAction(r,'approve')} title="Approve & Ship"
              style={{color:'var(--success)',fontSize:11,gap:4}}>
              <CheckCircle size={13}/> Approve
            </button>
          )}

          {/* Ship */}
          {canApprove&&r.status==='pending'&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>doAction(r,'ship')} title="Mark Shipped"
              style={{fontSize:11,gap:4}}>
              <Truck size={13}/> Ship
            </button>
          )}

          {/* Receive */}
          {canReceive&&r.status==='in_transit'&&(
            <button className="btn btn-primary btn-sm" onClick={()=>openReceive(r)} title="Receive">
              <Package size={13}/> Receive
            </button>
          )}

          {/* Cancel */}
          {canCreate&&!['received','cancelled'].includes(r.status)&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>doAction(r,'cancel')} title="Cancel"
              style={{color:'var(--danger)'}}>
              <XCircle size={13}/>
            </button>
          )}

          {/* Delete draft */}
          {canDelete&&r.status==='draft'&&(
            <button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={14}/></button>
          )}
        </div>
      )}/>

      {/* ── CREATE / EDIT ── */}
      <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)}
        title={modal==='new'?'New Stock Transfer':'Edit Transfer'} width={780}>

        {/* Workflow status bar */}
        <div style={{display:'flex',gap:0,marginBottom:18,borderRadius:8,overflow:'hidden',border:'1px solid var(--border)'}}>
          {[
            {k:'pending',label:'1. Submit',icon:<Clock size={12}/>},
            {k:'in_transit',label:'2. Ship',icon:<Truck size={12}/>},
            {k:'received',label:'3. Receive',icon:<Package size={12}/>},
          ].map((s,i)=>(
            <div key={s.k} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'8px',background:i===0?'var(--pm)':'var(--s2)',fontSize:11,fontWeight:700,color:i===0?'var(--primary)':'var(--td)',borderRight:i<2?'1px solid var(--border)':'none'}}>
              {s.icon}{s.label}
            </div>
          ))}
        </div>

        <div className="form-grid" style={{marginBottom:14}}>
          <FormInput label="From Branch *" value={String(form.from_branch_id||'')} onChange={v=>F('from_branch_id',v)}
            options={branchOpts}/>
          <FormInput label="To Branch *" value={String(form.to_branch_id||'')} onChange={v=>F('to_branch_id',v)}
            options={branchOpts.filter(o=>o.value!==String(form.from_branch_id))}/>
        </div>

        {/* Items table */}
        <div style={{marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',letterSpacing:.4}}>ITEMS TO TRANSFER</div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addItem} style={{fontSize:11}}>
              <Plus size={12}/> Add Line
            </button>
          </div>
          <div style={{overflowX:'auto',borderRadius:8,border:'1px solid var(--border)'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'var(--s2)'}}>
                  {['Product','UoM (from product)','Available','Quantity',...(canSeeCost?['Unit Cost ($)']:[]),''].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:['Quantity','Unit Cost ($)'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.items.map((row,i)=>(
                  <ItemRow key={i} row={row} idx={i} products={products}
                    onChange={updateItem} onRemove={removeItem} inventoryMap={inventoryMap}
                    canSeeCost={canSeeCost}/>
                ))}
                {form.items.length===0&&(
                  <tr><td colSpan={canSeeCost?6:5} style={{textAlign:'center',padding:20,color:'var(--td)',fontSize:13}}>No items — click "+ Add Line"</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <FormInput label="Notes" value={form.notes||''} onChange={v=>F('notes',v)} type="textarea"/>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Saving…</>:<><Send size={14}/> Submit Transfer</>}
          </button>
        </div>
      </Modal>

      {/* Detail */}
      <DetailModal open={modal==='view'} transfer={selTransfer} onClose={()=>setModal(null)} canSeeCost={canSeeCost}/>

      {/* Receive */}
      <ReceiveModal open={modal==='receive'} transfer={selTransfer}
        onClose={()=>setModal(null)} onDone={load}/>

      {/* Delete */}
      <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Transfer" width={400}>
        <p className="text-muted" style={{marginBottom:18}}>Delete this transfer permanently?</p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setDelId(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={del}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}
